import { Modal, Space, Select, Input, Button, message } from "antd";
import { CopyOutlined } from "@ant-design/icons";
import { useMemo, useState, useCallback } from "react";
import { useAppDispatch } from "../../../../app/hooks";
import { getBusinessesThunk } from "../../../businesses/businessesSlice";
import { IBusinessFull } from "../../../../interfaces/businessFull.interface";
import debounce from "lodash/debounce";
import {
  CreatePppLinkRequestBody,
  PppLinkOriginType,
  PppRedirectPage,
} from "../../../../interfaces/pppLink.interface";
import { createPppLinkThunk, listPppLinksThunk } from "../../pppLinksSlice";

interface CreatePppLinkModalProps {
  open: boolean;
  onClose: () => void;
}

// Admin-creatable subset. The enum also carries pages the backend routes to on
// its own (receipt, table-order, payment-result-pending, review-mock).
const CREATABLE_REDIRECT_PAGES = [
  PppRedirectPage.Review,
  PppRedirectPage.Loyalty,
  PppRedirectPage.Subscription,
];

export default function CreatePppLinkModal({
  open,
  onClose,
}: CreatePppLinkModalProps) {
  const dispatch = useAppDispatch();
  const [selectedBusinessId, setSelectedBusinessId] = useState<
    string | undefined
  >();
  const [businessOptions, setBusinessOptions] = useState<
    { value: string; label: string }[]
  >([]);
  const [redirectPage, setRedirectPage] = useState<PppRedirectPage>(
    PppRedirectPage.Review
  );
  const [originType, setOriginType] = useState<PppLinkOriginType>(
    PppLinkOriginType.NfcSticker
  );
  const [isSuccessOpen, setIsSuccessOpen] = useState(false);
  const [successUrl, setSuccessUrl] = useState<string | undefined>();
  const [loading, setLoading] = useState(false);

  const copyToClipboard = useCallback(async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      message.success("URL copied");
    } catch {
      const textarea = document.createElement("textarea");
      textarea.value = text;
      textarea.style.position = "fixed";
      textarea.style.opacity = "0";
      document.body.appendChild(textarea);
      textarea.focus();
      textarea.select();
      try {
        document.execCommand("copy");
        message.success("URL copied");
      } catch {
        message.error("Failed to copy URL");
      } finally {
        document.body.removeChild(textarea);
      }
    }
  }, []);

  const searchBusinesses = async (q?: string) => {
    const { payload }: any = await dispatch(
      getBusinessesThunk({
        currentPage: 1,
        pageSize: 10,
        name: q?.toLowerCase(),
      } as any)
    );
    const opts = (payload?.data || []).map((b: IBusinessFull) => ({
      value: b._id,
      label: `${b.businessName} (${b.rootUser?.email || "-"})`,
    }));
    setBusinessOptions(opts);
  };
  const debouncedSearch = useMemo(
    () => debounce((v: string) => searchBusinesses(v), 300),
    []
  );

  const handleOk = async () => {
    if (!selectedBusinessId) return;
    setLoading(true);
    try {
      const body: CreatePppLinkRequestBody = {
        businessId: selectedBusinessId,
        redirectPage,
        originType,
      };
      const created = await dispatch(createPppLinkThunk(body));
      const payload = (created as any)?.payload;
      const link = payload?.fullUrl ? payload : payload?.link;
      if (link?.fullUrl) {
        setSuccessUrl(link.fullUrl);
        setIsSuccessOpen(true);
      }
      setSelectedBusinessId(undefined);
      setRedirectPage(PppRedirectPage.Review);
      setOriginType(PppLinkOriginType.NfcSticker);
      onClose();
      dispatch(listPppLinksThunk({}));
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Modal
        title="Create PPP Link"
        open={open}
        onCancel={onClose}
        okText="Create"
        confirmLoading={loading}
        onOk={handleOk}
      >
        <Space direction="vertical" style={{ width: "100%" }}>
          <div>Business</div>
          <Select
            showSearch
            placeholder="Search business"
            filterOption={false}
            onSearch={(v) => debouncedSearch(v)}
            onFocus={() => searchBusinesses()}
            options={businessOptions}
            value={selectedBusinessId}
            style={{ width: "100%" }}
            onChange={(val) => setSelectedBusinessId(val)}
          />

          <div>Redirect Page</div>
          <Select
            value={redirectPage}
            onChange={setRedirectPage}
            options={CREATABLE_REDIRECT_PAGES.map((v) => ({
              value: v,
              label: v,
            }))}
          />

          <div>Origin Type</div>
          <Select
            value={originType}
            onChange={setOriginType}
            options={[
              { value: PppLinkOriginType.NfcBracelet, label: "NFC Bracelet" },
              { value: PppLinkOriginType.NfcSticker, label: "NFC Sticker" },
              { value: PppLinkOriginType.NfcCoaster, label: "NFC Coaster" },
            ]}
          />
        </Space>
      </Modal>

      <Modal
        title="PPP Link Created"
        open={isSuccessOpen}
        onCancel={() => setIsSuccessOpen(false)}
        footer={[
          <Button
            key="copy"
            type="primary"
            icon={<CopyOutlined />}
            onClick={() => successUrl && copyToClipboard(successUrl)}
          >
            Copy Link
          </Button>,
          <Button key="close" onClick={() => setIsSuccessOpen(false)}>
            Close
          </Button>,
        ]}
      >
        <Space direction="vertical" style={{ width: "100%" }}>
          <div>Link</div>
          <Input value={successUrl} readOnly />
        </Space>
      </Modal>
    </>
  );
}
